/* Copyright (c) 2020-present CloudIO Inc */

import { IoPagesHistory } from '@cloudio-saas/datasource-types';
import {
  useStateXAddToUndo,
  useStateXAtom,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import { Divider, IconButton, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Page } from 'componentregistry';
import { designPath, pageAtom } from 'designer/atoms';
import Icon from 'icons/Icon';
import {
  pageHistoryDiffOpenAtom,
  PageView,
  sidebarActivePageIdAtom,
} from 'layout/Sidebar/atoms';
import { usePlatform } from 'platform/PlatformHooks';
import React from 'react';
import { useStoreCurrentRecord, useStoreBusy } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import { useIoPagesStore } from 'store/utils/utils';
import WaveDots from 'utils/WaveDots';
import ExpandCollapseIcon from '../ExpandCollapseIcon';
import { selectionAtom } from './PagesHistory';
import PagesHistoryDiffDialog from './PagesHistoryDiffDialog';
import { isViewOnlyDeveloper } from 'root/useUser';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  title: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
}));

export default function PagesHistoryHeader({
  appUid,
  setView,
}: {
  appUid: string;
  setView: (_view: PageView) => void;
}): React.ReactElement {
  const store = useDataStore<IoPagesHistory>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPagesHistory',
    alias: 'IoPagesHistory-diff',
  });
  const pageStore = useIoPagesStore(appUid);
  const activePageId = useStateXAtomValue(sidebarActivePageIdAtom);
  const { record: rec } = useStoreCurrentRecord(pageStore);
  if (activePageId && activePageId !== rec?.pageUid) {
    // must be coming here directly using an URL
    const recordId = pageStore
      .recordsOrder()
      .find((rId) => pageStore.getRecord(rId).pageUid === activePageId);
    pageStore.setCurrentRecordId(recordId);
  }
  const busy = useStoreBusy(store);
  const [open, setOpen] = useStateXAtom(pageHistoryDiffOpenAtom);
  const classes = useStyles();
  const selection = useStateXAtomValue(selectionAtom);
  const platform = usePlatform();
  const addToUndo = useStateXAddToUndo('io');

  function handleClose() {
    setOpen(false);
  }

  if (!rec) {
    return <>Missing rec!</>;
  }

  const twoSelection = selection[0] && selection[1];
  const hasSelection = selection[0] || selection[1];
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  return (
    <div className={classes.root}>
      <IconButton
        data-testid="back-button"
        onClick={() => {
          store.reset();
          setView('list');
        }}
        className={classes.iconButton}
        aria-label="Back"
      >
        <Icon iconName="chevron-left" />
      </IconButton>
      <Typography className={classes.title}>Page Change History</Typography>
      {hasSelection ? (
        <Divider className={classes.divider} orientation="vertical" />
      ) : null}
      {!twoSelection && hasSelection ? (
        <IconButton
          disabled={isViewOnlyUser}
          data-tip="Restore Version"
          onClick={async () => {
            const version = selection[0] || selection[1];
            if (busy || !version) return;
            const r = await store.query({
              props: {
                filter: [
                  { appUid: { is: rec.appUid } },
                  { pageUid: { is: rec.pageUid } },
                  { version: { is: version } },
                ],
                projection: {
                  md: 1,
                },
                limit: 1,
              },
            });
            if (r) {
              const _rec = store.getCurrentRecord();
              const pageId = rec.pageUid;
              if (_rec && pageId) {
                if (!platform.resource().hasPageLoaded(appUid, pageId)) {
                  const pageResource = platform
                    .resource()
                    .refreshPage(appUid, pageId, false);
                  try {
                    pageResource?.fetch(() => {
                      addToUndo(designPath({ appUid, pageId }), pageId);
                      platform.setter(pageAtom, _rec.md as unknown as Page, {
                        params: { appUid, pageId },
                      });
                      platform.showWarning(
                        'Select version restored locally. To restore on the server, you must save the page after verifying.',
                      );
                    });
                  } catch (e) {
                    if (e instanceof Error) {
                      platform.showError(e.message);
                    }
                  }
                } else {
                  addToUndo(designPath({ appUid, pageId }), pageId);
                  platform.setter(pageAtom, _rec.md as unknown as Page, {
                    params: { appUid, pageId },
                  });
                  platform.showWarning(
                    'Select version restored locally. To restore on the server, you must save the page after verifying.',
                  );
                }
              }
            }
          }}
        >
          <Icon disabled={isViewOnlyUser} color="primary" iconName="history" />
        </IconButton>
      ) : null}
      {twoSelection ? (
        <IconButton
          disabled={busy}
          color="primary"
          data-tip="View difference between selected versions"
          aria-label="View difference between selected versions"
          onClick={async () => {
            if (busy) return;
            setOpen(true);
          }}
        >
          {busy ? <WaveDots active /> : <Icon color="primary" iconName="eye" />}
        </IconButton>
      ) : null}
      {hasSelection && !twoSelection && platform.pageId === rec.pageUid ? (
        <IconButton
          disabled={busy}
          color="primary"
          data-tip="View difference between the selected version and current page"
          aria-label="View difference between the selected version and current page"
          onClick={async () => {
            const version = selection[0] || selection[1];
            if (busy || !version) return;
            setOpen(true);
          }}
        >
          {busy ? <WaveDots active /> : <Icon color="primary" iconName="eye" />}
        </IconButton>
      ) : null}
      <ExpandCollapseIcon />
      {open ? (
        <PagesHistoryDiffDialog appUid={appUid} onClose={handleClose} />
      ) : null}
    </div>
  );
}
